import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import * as PR from "../../../prime-react/index";
import Layout from '../../../layouts/Layout';
import "./AlertTags.scss";
import { getPlayers, modifyTag, deleteAlertTag} from '../../../services/api';
import { authActions } from '../../../store/auth';
import { useDispatch, useSelector } from 'react-redux';
import { DateFormat, decodeHtml, dompurifyText, formatTime, getPlainText, pagecount, queryLimit, textSanitize } from '../../../utils/reuse';
import { useFormik } from "formik";
import * as Yup from 'yup';
import { ErrorMessage, useToast } from '../../../context/ToastContext';

const deleteTagErrorId = 'delete_tag_id'
const createUpdateApiError  = 'create_update_api_error'
const AlertTags = () => {
    const { showToast, clearError } = useToast();
    const [visible, setVisible] = useState(false);
    const [deletePopupVisible, setDeletePopupVisible] = useState(false);
    const [tagsData, setTagsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const searchedTag = useRef('')
    const [error, setError] = useState(null)
    const [updateData, setUpdateData] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const sortQuery = useRef({sort_by:'count', sort_order : 'desc' });

    useEffect(() => {
        dispatch(authActions.setBackPageParams({}));
    }, [dispatch]);
    const [page, setPage] = useState(1);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(pagecount + 1);
    const [last, setLast] = useState(true);
    const [paging, setPaging] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const tagCategoryOptions = useMemo(() => ([
        { label: 'Athlete/Team', value: 'athleteteam' },
        { label: 'Market', value: 'market' }
    ]), []);

    const sessionData = useSelector(state => state.auth?.sessionData);
    const role = useSelector(state => state.auth?.role);
    const headers = useMemo(() => {
        return { 
            sessionid: sessionData.sessionId
        };
    }, [sessionData.sessionId]);

    const gotoAlertList=(rowData)=>{
        navigate(`/alerts?tagId=${rowData?.id}&tagName=${rowData?.name}`);
    }

    const getTagsResponse = useCallback((searchQuery) => {
        setLoading(true);
        const obj = {
            offset: offset,
            limit: limit,
            "categories[]": activeIndex === 0 ? 'athleteteam' : 'market',
            all: true,
            ...sortQuery.current
        };

        if (searchQuery) {
            obj.search = searchQuery;
        }
        getPlayers(obj, headers, dispatch, response => {
            if(response.result === 'SUCCESS'){
                const responseData = response.data;
                const result = (responseData?.length > 0) ? responseData.slice(0, pagecount) : [];
                if (result.length > 0) {
                    setLast(responseData?.length <= pagecount);
                    setPaging(true)
                    setTagsData(result);
                } else {
                    setLast(true);
                    setPaging(false);
                    setTagsData([]);
                }
            } else {
                setLast(true);
                setPaging(false);
                setTagsData([]);
                // const error = response.error;
                // showToast(error.severity, error?.errorMsg || error?.summary);
            }
            setLoading(false);
        })
        // eslint-disable-next-line
    }, [dispatch, headers, limit, offset, showToast, activeIndex])

    const handlePageChange = (currentPage) =>{
        setPaging(false);
        setPage(currentPage);
        const offsetVal = (currentPage - 1) * pagecount;
        const limitVal = pagecount + 1;
        setOffset(offsetVal);
        setLimit(limitVal);
    }
    const trigger = useRef(null);

    const [searchQuery,setSearchQuery] = useState('')
    const searchTagHandler = (e) => {

        let shouldTriggerRefetch = false;
        const query = e.target.value ?? null;

        if (query.length < 3) {
            setSearchQuery(query)
            
            if(searchedTag.current !== ''){
                searchedTag.current = ''
                shouldTriggerRefetch = true;
            }
            setError('Please enter atleast 3 characters');
            setTimeout(() => {
                setError(null);
            }, 2000);
        } else if (query?.length <= queryLimit) {

            setError(null)
            setSearchQuery(query)
            if(searchedTag.current !== query){
                searchedTag.current = query
                shouldTriggerRefetch = true;
            }
        }else{
            setError(null)
            setError(`Search can be at most ${queryLimit} characters long`)
            setTimeout(() => {
                setError(null)
            }, 2000)
        }
        if(shouldTriggerRefetch){
            if (trigger.current) clearTimeout(trigger.current);

            trigger.current = setTimeout(() => {
                getTagsResponse(searchedTag.current);
            }, 1000);
        }
    }

    const onTabChange = (index) => {
        setActiveIndex(index);
        setPage(1);
        setOffset(0);
        setSearchQuery('');
        searchedTag.current = '';
        if (trigger.current) clearTimeout(trigger.current);
    }

    const initialValues = {
        id: updateData ? updateData.id : '',
        name: updateData ? updateData.name : '',
        category: updateData?.category ? updateData.category : '',
        description: updateData ? decodeHtml(updateData.description) : '',
    }

    const validationSchema = () => {
        return Yup.object().shape({
            name: Yup.string()
                .trim()
                .required('Name is required')
                .min(3, "Name should be at least 3 characters long")
                .max(50, "Name must not exceed 50 characters"),
            category: Yup.string().required('Category is required'),
            description: Yup.string()
                .trim()
                .test("valid-text", "Enter valid text", (value) =>  !(getPlainText(value) === 0 && value))
                .required('Description is required')
                .min(3, "Description should be at least 3 characters long")
                .max(100, "Description must not exceed 100 characters")
            });
    }

    const handleSubmit = values => {
        setSubmitting(true);
        clearError(createUpdateApiError)
        const getResponse = response => {
            const summary = updateData ? 'updated' : 'created';
            if (response.result === "SUCCESS") {
                setLoading(false);
                setVisible(false);
                getTagsResponse(searchedTag.current);
                formik.resetForm();
                setSubmitting(false);
                showToast("success", `Tag ${summary} successfully`);
            } else {
                setLoading(false);
                setSubmitting(false);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary, createUpdateApiError);
            }
        }
        const addRequestObj = {
            name: values.name,
            category: values.category,
            description: textSanitize(values.description)
        }
        const updateRequestObj = {
            id: +values.id,
            name: values.name,
            category: values.category,
            description: textSanitize(values.description)
        }
        const requestObj = updateData ? updateRequestObj : addRequestObj;
        modifyTag(requestObj, headers, dispatch, getResponse);
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true
    });

    useEffect(() => {
        getTagsResponse(searchedTag.current);
        // eslint-disable-next-line
    }, [getTagsResponse]);

    const action = (rowData) => {
        return (
            <div className="flex gap-2 align-items-center">
                <PR.Button label="View Alerts" type='submit' severity="secondary" outlined className="view-details-button" onClick={() => gotoAlertList(rowData)} disabled={!rowData?.alertsCount} />
                <PR.Button icon="pi pi-pencil" outlined className="view-details-button only-icon" type='submit' onClick={() => {setVisible(true); setUpdateData(rowData)}} />
                <PR.Button icon="pi pi-trash" outlined className="view-details-button only-icon" type='submit' onClick={() => {setDeletePopupVisible(true); setUpdateData(rowData)}} />
            </div>
        );
    };

    const footerDelete = (
        <div>
            <ErrorMessage id={deleteTagErrorId} />
            <PR.Button label="Cancel" onClick={() =>{clearError(deleteTagErrorId); setDeletePopupVisible(false)}} outlined className="logout-btns" />
            <PR.Button label="Delete" onClick={() => handleDeleteTag()} outlined autoFocus className="logout-btns click-btn delete" />
        </div>
    );

    const handleDeleteTag = () => {
        if(updateData?.alertsCount>0){
            showToast("error", "Tags with alerts attached to them can't be deleted", deleteTagErrorId);
            return;
          }
          deleteAlertTag(updateData.id,headers,dispatch,(response) =>{
            if (response.result === "SUCCESS") {
                setDeletePopupVisible(false)
              showToast("success", "Tag deleted successfully");
              getTagsResponse(searchedTag.current);
            } else {
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary, deleteTagErrorId);
            }
          })
    }

    const paginationTemplate = {
        layout: 'CurrentPageReport PrevPageLink NextPageLink',
        CurrentPageReport: (options) => {
            return ( <span className='current-page'>Page {page}</span> )
        },
        PrevPageLink: (options) =>{
            return (<PR.Button className='pagination-buttons'
                        onClick={() => handlePageChange(page-1)}
                        disabled={paging ? page <= 1 ? true : false :true}>Previous</PR.Button>
                    )
        },
        NextPageLink: (options) =>{
            return (<PR.Button disabled={paging ? last ? true : false : true} onClick={() => handlePageChange(page+1)} 
            className='pagination-buttons'>Next</PR.Button>)
        }
    };
    
    const getHeader = () => {
        return (
            <div className="filter-section">
                <div className='flex gap-2 align-items-center '>
                    <div className='p-input-icon-right'>
                        <span className="p-input-icon-left flex align-items-center search-box ">
                            <i className="pi pi-search" />
                            <PR.InputText name="searchTag" placeholder="Search by Tag Name.." value={searchQuery} className="search-field" onChange={searchTagHandler} autoComplete='off' />
                        </span>
                        <span className="right-clear-icon">
                            {!!searchQuery && <i style={{ cursor: 'pointer', color:"#6c757d" }} onClick={() => searchTagHandler({ target: { value: "" } })} className="pi pi-times text-sm mr-2" />}
                        </span>
                    </div>
                    <PR.Button label="Create Tag" icon="pi pi-plus" className="create-button" onClick={() => { setVisible(true); setUpdateData(""); }} />
                </div>
                {error && <p className="error-message mt-1">{error}</p>}
            </div>
        );
    }

    const alertsCountDisplay = (rowData) => {
        const count = rowData?.alertsCount ?? 0;
        return `${count}`;
    }
    const alertDescription = (rowData) => {
        return (<>
        <p dangerouslySetInnerHTML={{__html: dompurifyText(rowData?.description)}}></p>
        </>)
    }

    const customSort = ({sortField,sortOrder}) => {
        sortQuery.current = {sort_order: sortOrder === -1 ? 'desc' : 'asc', sort_by: sortField };
        setPage(1);
        setOffset(0)
        getTagsResponse(searchedTag.current);
    };

    const createdAtDateDisplay = (rowData) => {
        return (
            <div className="flex align-items-center gap-3">
                <div className="event-column">
                    <p>{DateFormat(rowData.createdAt)}</p>
                    <span>{formatTime(rowData.createdAt)}</span>
                </div>
            </div>
        );
    };

    const tagTableTemplate = () => {
        return (
            <div className="grid grid-nogutter">
                <div className="card col-12 relative">
                    <PR.DataTable 
                        value={tagsData}
                        stripedRows 
                        className="data-table"
                        paginatorTemplate={paginationTemplate}
                        paginator
                        rows={pagecount}
                        header={getHeader()}
                        loading={loading}
                        sortOrder={ sortQuery.current.sort_order === 'desc'? -1:1}
                        sortField={sortQuery.current.sort_by}
                        onSort={customSort}
                    >
                        <PR.Column field='name' header="Tag name"></PR.Column>
                        <PR.Column body={alertDescription}  header="Description"></PR.Column>
                        <PR.Column body={alertsCountDisplay} header="Alerts" sortable sortField="count"></PR.Column>
                        <PR.Column body={createdAtDateDisplay} header="Created" sortable sortField="created"></PR.Column>
                        <PR.Column body={action} header="Action" className="last-column"></PR.Column>
                    </PR.DataTable>
                </div>
            </div>
        );
    }

    return (
        <>  
            <Layout backBtn={"home"} backBtnLabel="Home">
                <section className="all-alerts-section all-tags-section mt-1">
                    <div className="m-0">
                        <h1>Alert Tags</h1>
                    </div>
                    <div className={`alerts-tab ${role === 'analyst' ? 'analyst-tab' : ''}`}>
                        <PR.TabView activeIndex={activeIndex} onTabChange={(e) => onTabChange(e.index)}>
                            <PR.TabPanel header="Athlete/Team Tags">
                                {tagTableTemplate()}
                            </PR.TabPanel>
                            <PR.TabPanel header="Market Tags">
                                {tagTableTemplate()}
                            </PR.TabPanel>
                        </PR.TabView>
                    </div>
                </section>

                {/* Create/Edit Tag Dialog */}
                <PR.Dialog visible={visible} onHide={() => {formik.resetForm(); setVisible(false); setSubmitting(false); clearError(createUpdateApiError)}} className="logout-dialog response-dialog tags-dialog">
                    <div className="dialog-header">
                        <div className='mb-5'>
                            <h2>{updateData ? 'Update Tag' : 'Create Tag'}</h2>
                            <p>Categorize the alert according to the relevant tag</p>
                        </div>
                    </div>
                    <form autoComplete="off"  onSubmit={formik.handleSubmit}>
                        <div className="dialog-body-section">
                            <div className="p-float-label-none hidden">
                                <span>
                                    <PR.InputText placeholder="id" name="id" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.id} className="w-full" />
                                </span>
                            </div>
                            <div className="flex flex-column form-input gap-2 mb-3">
                                <label htmlFor="name">Tag name<span className='important' >*</span></label>
                                <PR.InputText id="name" name="name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} placeholder='Enter Tag name' autoComplete="off" />
                                {formik.errors.name && formik.touched.name
                                    ? <div className='error-message'>{formik.errors.name}</div>
                                    : ''
                                }
                            </div>
                            <div className="flex flex-column form-input gap-2 mb-3">
                                <label htmlFor="category">Category<span className='important'>*</span></label>
                                <PR.Dropdown
                                    inputId="category"
                                    name="category"
                                    value={formik.values.category || null}
                                    options={tagCategoryOptions}
                                    onChange={(e) => formik.setFieldValue('category', e.value || '')}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select category"
                                    className="multi-select-dropdown"
                                    disabled={!!updateData}
                                />
                                {formik.errors.category && formik.touched.category
                                    ? <div className='error-message'>{formik.errors.category}</div>
                                    : ''
                                }
                            </div>
                            <div className="flex flex-column gap-2">
                                <label htmlFor="description">Tag description<span className='important'>*</span></label>
                                <div className="card">
                                    <PR.InputTextarea 
                                     id='description'
                                     name="description" placeholder='Something about tag..' className="description-textarea" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.description}/>
                                    {formik.errors.description && formik.touched.description
                                    ? <div className='error-message'>{formik.errors.description}</div>
                                    : ''
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='dialog-footer'>
                            <ErrorMessage id={createUpdateApiError} />
                            <PR.Button label="Cancel" type="button" onClick={() => {formik.resetForm(); setVisible(false); setSubmitting(false); clearError(createUpdateApiError)}} outlined className="logout-btns" />
                            <PR.Button label={updateData ? "Update Tag" : "Create Tag"} type="submit" outlined autoFocus className="logout-btns click-btn" disabled={submitting}/>
                        </div>
                    </form> 
                </PR.Dialog>

                {/* Delete Tag Dialog */}
                <PR.Dialog visible={deletePopupVisible} footer={footerDelete} onHide={() => setDeletePopupVisible(false)} className="logout-dialog response-dialog tags-dialog delete-tag">
                    <div className="dialog-header">
                        <div className='gap-2'>
                            <h2>Delete Tag</h2>
                            <p>Are you sure you want to delete this tag?</p>
                        </div>
                    </div>
                </PR.Dialog>
            </Layout>
        </>
    );
};

export default AlertTags;
